/**
 * Mesa Vision C T7 — regen híbrido (RF-15b). Pure helpers, no storage:
 * - parametric: turn the annotation text into a knob patch the user can edit before confirming
 * - chat: prompt the copilot and read back its `[CMD:STUDY_PATCH:{json}]` marker as a proposal
 */

import { proceduralPartLayout } from "@/lib/studyProceduralLayout";
import type { StudyAnnotation, StudyPackage, StudyParamPatch, StudyPart } from "@/lib/studyPackageTypes";

/** Only footprint knobs are accepted from chat — they drive the masses and derived views. */
export const CHAT_PATCH_PARAMS = ["w", "d", "h"] as const;
const PARAM_LABEL: Record<string, string> = { w: "Ancho", d: "Fondo", h: "Alto" };
export const PARAM_MAX = 1000;

const round = (n: number) => Math.round(n * 100) / 100;

function clampParam(part: StudyPart | undefined, paramId: string, v: number): number {
  const p = part?.params?.find((x) => x.id === paramId);
  let out = Math.min(Math.max(v, 0.01), PARAM_MAX);
  if (p?.min !== undefined) out = Math.max(out, p.min);
  if (p?.max !== undefined) out = Math.min(out, p.max);
  return round(out);
}

type Intent = { paramId: string; factor?: number; value?: number };

const DIM_WORDS: Record<string, RegExp> = {
  h: /\b(alt[oa]|altura|height|tall)\b/gi,
  w: /\b(anch[oa]|anchura|width|wide)\b/gi,
  d: /\b(fondo|profund\w*|depth|deep|larg[oa]|length)\b/gi,
};

// No bare "taller": in Spanish it is a room (workshop), not "higher".
const RELATIVE: { re: RegExp; paramId: string; factor: number }[] = [
  { re: /\b(m[aá]s alt[oa]|subir|elevar|higher|raise)\b/i, paramId: "h", factor: 1.25 },
  { re: /\b(m[aá]s baj[oa]|bajar|lower|shorter)\b/i, paramId: "h", factor: 0.8 },
  { re: /\b(m[aá]s anch[oa]|ampliar|ensanchar|wider)\b/i, paramId: "w", factor: 1.25 },
  { re: /\b(m[aá]s angost[oa]|m[aá]s estrech[oa]|estrechar|narrower)\b/i, paramId: "w", factor: 0.8 },
  { re: /\b(m[aá]s profund[oa]|m[aá]s larg[oa]|alargar|deeper|longer)\b/i, paramId: "d", factor: 1.25 },
  { re: /\b(menos profund[oa]|m[aá]s cort[oa]|acortar|shallower)\b/i, paramId: "d", factor: 0.8 },
];

/** Closest dimension word to [from, to) inside the clause, before or after the number. */
function nearestDim(clause: string, from: number, to: number): string | null {
  let best: { id: string; dist: number } | null = null;
  for (const [id, re] of Object.entries(DIM_WORDS)) {
    for (const m of clause.matchAll(re)) {
      const at = m.index ?? 0;
      const dist = at >= to ? at - to : from - (at + m[0].length);
      if (dist >= 0 && (!best || dist < best.dist)) best = { id, dist };
    }
  }
  return best?.id ?? null;
}

/** "a 2.4 m", "2,4m", "240 cm de alto" → meters. A unit is required so "Vano 2" never reads as 2 m. */
function absoluteIntents(clause: string): Intent[] {
  const out: Intent[] = [];
  const re = /(\d+(?:[.,]\d+)?)\s*(mm|cm|metros?|m|ft|pies?)\b/gi;
  for (const m of clause.matchAll(re)) {
    let v = parseFloat(m[1].replace(",", "."));
    const unit = m[2].toLowerCase();
    if (unit === "cm") v /= 100;
    else if (unit === "mm") v /= 1000;
    else if (unit.startsWith("ft") || unit.startsWith("pie")) v *= 0.3048;
    const at = m.index ?? 0;
    const paramId =
      nearestDim(clause, at, at + m[0].length) ?? (/\b(subir|bajar|elevar)\b/i.test(clause) ? "h" : null);
    if (paramId && v > 0) out.push({ paramId, value: v });
  }
  return out;
}

function intentsFrom(clause: string): Intent[] {
  const abs = absoluteIntents(clause);
  const rel = RELATIVE.filter((r) => r.re.test(clause) && !abs.some((a) => a.paramId === r.paramId)).map(
    (r) => ({ paramId: r.paramId, factor: r.factor })
  );
  return [...abs, ...rel];
}

function namedIn(clause: string, parts: StudyPart[]): StudyPart[] {
  const t = clause.toLowerCase();
  return parts.filter((p) => {
    const base = p.name.toLowerCase().replace(/\s*\d+$/, "");
    return (base.length > 2 && t.includes(base)) || (p.role.length > 2 && t.includes(p.role.toLowerCase()));
  });
}

/**
 * Splits the note into clauses ("…, …", "… y …"); each clause's intents go to the parts it names,
 * or to the previous clause's parts, or — if no part was named yet — to every annotated part.
 */
function intentsByPart(text: string, parts: StudyPart[]): Map<string, Intent[]> {
  const clauses = text.split(/[,;](?:\s|$)|\.(?:\s|$)|\s+(?:y|e|and)\s+/i).filter((c) => c.trim());
  const byPart = new Map<string, Intent[]>();
  let subject: StudyPart[] | null = null;
  for (const clause of clauses) {
    const named = namedIn(clause, parts);
    if (named.length > 0) subject = named;
    for (const part of subject ?? parts) {
      const list = byPart.get(part.id) ?? [];
      for (const intent of intentsFrom(clause)) {
        // First explicit value wins over later relative hints for the same knob.
        if (!list.some((i) => i.paramId === intent.paramId)) list.push(intent);
      }
      byPart.set(part.id, list);
    }
  }
  return byPart;
}

/**
 * Parametric proposal: every knob of the annotated parts, prefilled with what the text implies.
 * Unchanged rows (to === from) stay editable so the user can steer before confirming.
 */
export function suggestParametricPatch(pkg: StudyPackage, ann: StudyAnnotation): StudyParamPatch[] {
  const parts = ann.partIds.map((id) => pkg.parts.find((p) => p.id === id)).filter(Boolean) as StudyPart[];
  const byPart = intentsByPart(ann.text, parts);
  const rows: StudyParamPatch[] = [];
  for (const part of parts) {
    for (const param of part.params ?? []) {
      let to = param.value;
      const intent = byPart.get(part.id)?.find((i) => i.paramId === param.id);
      if (intent?.value !== undefined) {
        to = param.unit === "mm" ? intent.value * 1000 : intent.value;
      } else if (intent?.factor !== undefined) {
        to = param.value * intent.factor;
      }
      rows.push({
        partId: part.id,
        paramId: param.id,
        label: param.label,
        unit: param.unit,
        from: param.value,
        to: clampParam(part, param.id, to),
      });
    }
  }
  return rows;
}

/** Manual fallback for the chat path: seed w/d/h from the current procedural mass so the user can type values. */
export function manualKnobPatch(pkg: StudyPackage, ann: StudyAnnotation): StudyParamPatch[] {
  const boxes = new Map(proceduralPartLayout(pkg.parts).map((b) => [b.partId, b]));
  const rows: StudyParamPatch[] = [];
  for (const id of ann.partIds) {
    const part = pkg.parts.find((p) => p.id === id);
    const box = boxes.get(id);
    if (!part || !box) continue;
    for (const paramId of CHAT_PATCH_PARAMS) {
      const existing = part.params?.find((x) => x.id === paramId);
      const current = existing?.value ?? box[paramId];
      rows.push({
        partId: id,
        paramId,
        label: existing?.label ?? PARAM_LABEL[paramId],
        unit: existing?.unit ?? "m",
        from: existing ? existing.value : undefined,
        to: round(current),
      });
    }
  }
  return rows;
}

export function patchHasChanges(patch: StudyParamPatch[] | undefined): boolean {
  return Boolean(patch?.some((r) => r.from === undefined || Math.abs(r.to - r.from) > 1e-9));
}

const knob = (p: StudyPart, id: "w" | "d") => p.params?.find((k) => k.id === id)?.value ?? 1;
const spanOverlap = (a0: number, a1: number, b0: number, b1: number) => Math.min(a1, b1) - Math.max(a0, b0) > 1e-6;

/**
 * Placed parts (with `origin`) don't reflow on their own: when a part grows or shrinks, neighbours
 * that start at or beyond its old far edge — and share its row / column span — shift by the delta,
 * so a seeded plan stays contiguous instead of rooms overlapping.
 */
export function reflowOrigins(before: StudyPart[], after: StudyPart[]): StudyPart[] {
  const prev = new Map(before.map((p) => [p.id, p]));
  const shift = new Map<string, { dx: number; dz: number }>();
  for (const part of after) {
    const old = prev.get(part.id);
    if (!part.origin || !old?.origin) continue;
    const dw = knob(part, "w") - knob(old, "w");
    const dd = knob(part, "d") - knob(old, "d");
    if (Math.abs(dw) < 1e-6 && Math.abs(dd) < 1e-6) continue;
    const { x, z } = old.origin;
    const right = x + knob(old, "w");
    const back = z + knob(old, "d");
    for (const other of before) {
      if (other.id === part.id || !other.origin) continue;
      const s = shift.get(other.id) ?? { dx: 0, dz: 0 };
      const ox = other.origin.x;
      const oz = other.origin.z;
      if (dw && ox >= right - 1e-6 && spanOverlap(oz, oz + knob(other, "d"), z, back)) s.dx += dw;
      if (dd && oz >= back - 1e-6) s.dz += dd;
      shift.set(other.id, s);
    }
  }
  return after.map((p) => {
    const s = shift.get(p.id);
    if (!p.origin || !s || (!s.dx && !s.dz)) return p;
    const r = (n: number) => Math.round(n * 100) / 100;
    return { ...p, origin: { x: r(p.origin.x + s.dx), z: r(p.origin.z + s.dz) } };
  });
}

/** Applies a patch to parts (adds missing knobs, clamps) and reflows placed neighbours. Pure. */
export function applyPatchToParts(parts: StudyPart[], patch: StudyParamPatch[]): StudyPart[] {
  return reflowOrigins(parts, applyKnobs(parts, patch));
}

function applyKnobs(parts: StudyPart[], patch: StudyParamPatch[]): StudyPart[] {
  return parts.map((part) => {
    const rows = patch.filter((r) => r.partId === part.id);
    if (rows.length === 0) return part;
    const params = [...(part.params ?? [])];
    for (const r of rows) {
      const i = params.findIndex((p) => p.id === r.paramId);
      const value = clampParam(part, r.paramId, r.to);
      if (i >= 0) params[i] = { ...params[i], value };
      else params.push({ id: r.paramId, label: r.label, unit: r.unit, value });
    }
    return { ...part, params };
  });
}

export function buildCopilotRegenPrompt(pkg: StudyPackage, ann: StudyAnnotation, lang: "es" | "en" = "es"): string {
  const boxes = new Map(proceduralPartLayout(pkg.parts).map((b) => [b.partId, b]));
  const lines = ann.partIds.map((id) => {
    const p = pkg.parts.find((x) => x.id === id);
    const b = boxes.get(id);
    const knobs = p?.params?.length
      ? p.params.map((k) => `${k.id}=${k.value}${k.unit ?? ""}`).join(", ")
      : b
        ? `sin parámetros (masa actual w=${round(b.w)} d=${round(b.d)} h=${round(b.h)})`
        : "sin parámetros";
    return `- ${p?.name ?? id} (id ${id}, rol ${p?.role ?? "?"}): ${knobs}`;
  });
  const example = JSON.stringify({
    annotationId: ann.id,
    patches: [{ partId: ann.partIds[0], params: { w: 1, d: 1, h: 1 } }],
    note: lang === "en" ? "short reason" : "motivo breve",
  });
  const head =
    lang === "en"
      ? `[Mesa study · annotation ${ann.id}] Project "${pkg.title}" (template ${pkg.templateId}). Propose a regen for these parts:`
      : // No "anota…/tarea/pendiente/agrega" here: the backend quick-commands turn those into a real task.
        `[Estudio Mesa · ref ${ann.id}] Proyecto «${pkg.title}» (plantilla ${pkg.templateId}). Propón una regeneración para estas partes:`;
  const tail =
    lang === "en"
      ? `Requested change: "${ann.text}"\nExplain briefly, then end with EXACTLY one marker (same units as the current params, meters if none; only these part ids; only w, d, h):\n[CMD:STUDY_PATCH:${example}]\nNothing is applied until I confirm in Mesa.`
      : `Cambio pedido: «${ann.text}»\nExplica breve y termina con EXACTAMENTE un marcador (mismas unidades que los parámetros actuales, metros si no hay; solo estos ids; solo w, d, h):\n[CMD:STUDY_PATCH:${example}]\nNo se aplica nada hasta que confirme en Mesa.`;
  return [head, ...lines, tail].join("\n");
}

const MARKER = "[CMD:STUDY_PATCH:";

/** String-aware scan: returns the JSON object text starting at `start` (a "{"), or null. */
function scanJsonObject(text: string, start: number): string | null {
  if (text[start] !== "{") return null;
  let depth = 0;
  let inStr = false;
  for (let i = start; i < text.length; i++) {
    const c = text[i];
    if (inStr) {
      if (c === "\\") i++;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') inStr = true;
    else if (c === "{") depth++;
    else if (c === "}" && --depth === 0) return text.slice(start, i + 1);
  }
  return null;
}

/** Finds every `[CMD:STUDY_PATCH:{…}]` marker; returns parsed payloads and the text without them. */
export function extractStudyPatchMarkers(text: string): { payloads: unknown[]; stripped: string } {
  const payloads: unknown[] = [];
  let stripped = "";
  let cursor = 0;
  for (let idx = text.indexOf(MARKER); idx !== -1; idx = text.indexOf(MARKER, cursor)) {
    const json = scanJsonObject(text, idx + MARKER.length);
    const end = json ? idx + MARKER.length + json.length : -1;
    if (!json || text[end] !== "]") {
      stripped += text.slice(cursor, idx + MARKER.length);
      cursor = idx + MARKER.length;
      continue;
    }
    try {
      payloads.push(JSON.parse(json));
    } catch {
      /* malformed marker: drop it from the visible text, ignore payload */
    }
    stripped += text.slice(cursor, idx);
    cursor = end + 1;
  }
  stripped += text.slice(cursor);
  return { payloads, stripped };
}

export type ChatPatchResult =
  | { ok: true; annotationId: string; patch: StudyParamPatch[]; note?: string }
  | { ok: false; reason: string };

/**
 * Validates a copilot payload against the annotation: known annotation, only its parts,
 * only w/d/h, finite positive numbers. Anything else is rejected, never partially applied.
 */
export function validateChatPatch(pkg: StudyPackage, payload: unknown): ChatPatchResult {
  const p = payload as { annotationId?: unknown; patches?: unknown; note?: unknown };
  if (!p || typeof p.annotationId !== "string") return { ok: false, reason: "annotationId" };
  const ann = pkg.annotations.find((a) => a.id === p.annotationId);
  if (!ann || ann.status !== "proposed") return { ok: false, reason: "annotation_not_proposed" };
  if (!Array.isArray(p.patches) || p.patches.length === 0) return { ok: false, reason: "patches" };
  const rows: StudyParamPatch[] = [];
  for (const raw of p.patches) {
    const r = raw as { partId?: unknown; params?: unknown };
    if (typeof r?.partId !== "string" || !ann.partIds.includes(r.partId)) return { ok: false, reason: "partId" };
    const part = pkg.parts.find((x) => x.id === r.partId);
    if (!part || !r.params || typeof r.params !== "object") return { ok: false, reason: "params" };
    for (const [paramId, v] of Object.entries(r.params as Record<string, unknown>)) {
      if (!(CHAT_PATCH_PARAMS as readonly string[]).includes(paramId)) return { ok: false, reason: `param:${paramId}` };
      if (typeof v !== "number" || !Number.isFinite(v) || v <= 0 || v > PARAM_MAX) {
        return { ok: false, reason: `value:${paramId}` };
      }
      const existing = part.params?.find((x) => x.id === paramId);
      rows.push({
        partId: part.id,
        paramId,
        label: existing?.label ?? PARAM_LABEL[paramId],
        unit: existing?.unit ?? "m",
        from: existing?.value,
        to: clampParam(part, paramId, v),
      });
    }
  }
  const note = typeof p.note === "string" ? p.note.slice(0, 500) : undefined;
  return { ok: true, annotationId: ann.id, patch: rows, note };
}
